import { useEffect, useState, type ReactNode } from 'react';
import { usePageContext } from 'vike-react/usePageContext';
import {
  Accessibility,
  ArrowRight,
  ArrowUpRight,
  CalendarDays,
  Cookie,
  FileText,
  Mail,
  ScrollText,
  Shield,
  type LucideIcon,
} from 'lucide-react';
import { Container } from './Container';
import { BreadcrumbListLd } from '../seo/jsonld/BreadcrumbList';
import { ROUTES } from '@shared/constants/routes';
import { cn } from '../../lib/cn';

interface LegalPageMeta {
  href: string;
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  category: 'Légal' | 'RGPD' | 'Commercial' | 'Engagement';
}

const LEGAL_PAGES: LegalPageMeta[] = [
  {
    href: ROUTES.legal.mentions,
    label: 'Mentions légales',
    shortLabel: 'Mentions',
    icon: FileText,
    category: 'Légal',
  },
  {
    href: ROUTES.legal.confidentialite,
    label: 'Politique de confidentialité',
    shortLabel: 'Confidentialité',
    icon: Shield,
    category: 'RGPD',
  },
  {
    href: ROUTES.legal.cookies,
    label: 'Cookies',
    shortLabel: 'Cookies',
    icon: Cookie,
    category: 'RGPD',
  },
  {
    href: ROUTES.legal.cgv,
    label: 'CGV — Courtier énergie',
    shortLabel: 'CGV',
    icon: ScrollText,
    category: 'Commercial',
  },
  {
    href: ROUTES.legal.accessibilite,
    label: 'Déclaration d’accessibilité',
    shortLabel: 'Accessibilité',
    icon: Accessibility,
    category: 'Engagement',
  },
];

interface LegalLayoutProps {
  title: string;
  url: string;
  lastUpdated?: string;
  children: ReactNode;
}

export function LegalLayout({ title, url, lastUpdated, children }: LegalLayoutProps) {
  const ctx = usePageContext() as { urlPathname?: string };
  const pathname = ctx.urlPathname ?? '';

  const current = LEGAL_PAGES.find((p) => pathname.startsWith(p.href));
  const category = current?.category ?? 'Légal';

  const currentIndex = LEGAL_PAGES.findIndex((p) => p === current);
  const prev = currentIndex > 0 ? LEGAL_PAGES[currentIndex - 1] : undefined;
  const next =
    currentIndex >= 0 && currentIndex < LEGAL_PAGES.length - 1
      ? LEGAL_PAGES[currentIndex + 1]
      : undefined;

  const [readingTime, setReadingTime] = useState<number | null>(null);

  useEffect(() => {
    const article = document.getElementById('legal-content');
    if (!article) return;
    const text = article.textContent ?? '';
    const words = text.trim().split(/\s+/).length;
    setReadingTime(Math.max(1, Math.round(words / 220)));
  }, []);

  return (
    <div className="relative bg-stone-50/40">
      {/* Halo discret en haut */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[280px] bg-gradient-to-b from-brand-50/60 to-transparent"
      />

      {/* ─────────── HERO ─────────── */}
      <section className="relative pt-10 pb-8 md:pt-14">
        <Container>
          <div className="flex flex-col items-start gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-paper px-2.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-stone-600 shadow-soft">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
              {category}
            </span>

            <h1 className="text-balance font-display text-[1.5rem] font-bold leading-[1.15] tracking-tight text-ink sm:text-[1.75rem] md:text-[2rem]">
              {title}
            </h1>

            <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12.5px] text-stone-500">
              {lastUpdated && (
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5" strokeWidth={2} />
                  Mis à jour le {lastUpdated}
                </span>
              )}
              {readingTime !== null && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-1 w-1 rounded-full bg-stone-300" />
                  {readingTime} min de lecture
                </span>
              )}
            </div>
          </div>
        </Container>
      </section>

      {/* ─────────── BODY : sidebar + content ─────────── */}
      <section className="relative pb-24">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[260px,1fr] lg:gap-14">
            {/* ─── Sidebar nav ─── */}
            <aside className="lg:sticky lg:top-24 lg:self-start">
              <p className="px-3 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-stone-400">
                Espace légal
              </p>
              <nav aria-label="Documents légaux" className="mt-3 flex flex-col gap-0.5">
                {LEGAL_PAGES.map((p) => {
                  const Icon = p.icon;
                  const isActive = pathname.startsWith(p.href);
                  return (
                    <a
                      key={p.href}
                      href={p.href}
                      aria-current={isActive ? 'page' : undefined}
                      className={cn(
                        'group/link relative inline-flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13.5px] font-medium transition-all duration-200 ease-out',
                        isActive
                          ? 'bg-paper text-ink shadow-soft ring-1 ring-stone-200/70'
                          : 'text-stone-600 hover:bg-stone-100/80 hover:text-ink',
                      )}
                    >
                      {isActive && (
                        <span
                          aria-hidden
                          className="absolute -left-1 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-gradient-to-b from-brand-500 to-brand-700"
                        />
                      )}
                      <Icon
                        className={cn(
                          'h-4 w-4 shrink-0 transition-colors duration-200 ease-out',
                          isActive ? 'text-brand-600' : 'text-stone-400 group-hover/link:text-stone-600',
                        )}
                        strokeWidth={1.8}
                      />
                      <span className="truncate">{p.shortLabel}</span>
                    </a>
                  );
                })}
              </nav>

              {/* CTA contact dans la sidebar */}
              <div className="mt-8 hidden rounded-2xl border border-stone-200/70 bg-paper p-4 shadow-soft lg:block">
                <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                  Une question ?
                </p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-stone-600">
                  Contactez notre DPO ou le service juridique.
                </p>
                <a
                  href={`mailto:dpo@solutionecofrance.fr`}
                  className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-700 hover:text-brand-800"
                >
                  <Mail className="h-3.5 w-3.5" strokeWidth={2} />
                  dpo@solutionecofrance.fr
                </a>
              </div>
            </aside>

            {/* ─── Content ─── */}
            <article className="min-w-0">
              <div className="rounded-3xl border border-stone-200/70 bg-paper p-8 shadow-soft sm:p-10 md:p-12">
                <div id="legal-content" className="legal-prose">
                  {children}
                </div>
              </div>

              {/* Pagination prev/next */}
              {(prev || next) && (
                <nav
                  aria-label="Pagination documents légaux"
                  className="mt-8 grid gap-3 sm:grid-cols-2"
                >
                  {prev ? (
                    <a
                      href={prev.href}
                      className="group/nav inline-flex flex-col rounded-2xl border border-stone-200/70 bg-paper p-5 shadow-soft transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-elevated"
                    >
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                        <ArrowRight className="h-3 w-3 rotate-180" strokeWidth={2.2} />
                        Précédent
                      </span>
                      <span className="mt-1.5 text-[14px] font-medium text-ink group-hover/nav:text-brand-700">
                        {prev.label}
                      </span>
                    </a>
                  ) : (
                    <div />
                  )}
                  {next ? (
                    <a
                      href={next.href}
                      className="group/nav inline-flex flex-col rounded-2xl border border-stone-200/70 bg-paper p-5 text-right shadow-soft transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-elevated"
                    >
                      <span className="inline-flex items-center justify-end gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                        Suivant
                        <ArrowRight className="h-3 w-3" strokeWidth={2.2} />
                      </span>
                      <span className="mt-1.5 text-[14px] font-medium text-ink group-hover/nav:text-brand-700">
                        {next.label}
                      </span>
                    </a>
                  ) : (
                    <div />
                  )}
                </nav>
              )}

              {/* Footer page : CTA contact (mobile uniquement) */}
              <div className="mt-8 rounded-2xl border border-stone-200/70 bg-paper p-5 shadow-soft lg:hidden">
                <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                  Une question juridique ?
                </p>
                <a
                  href={`mailto:dpo@solutionecofrance.fr`}
                  className="mt-2 inline-flex items-center gap-1.5 text-[14px] font-medium text-brand-700"
                >
                  <Mail className="h-4 w-4" strokeWidth={2} />
                  dpo@solutionecofrance.fr
                  <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.2} />
                </a>
              </div>
            </article>
          </div>
        </Container>
      </section>

      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'Légal', url: 'https://solutionecofrance.fr/legal' },
          { name: title, url },
        ]}
      />
    </div>
  );
}
